/** @jsx jsx */
// TODO: remove eslint statement when 'css' is implemented
import { jsx } from '@emotion/react';
// eslint-disable-next-line no-unused-vars
import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';

import pick from 'lodash-es/pick';
import { primaryColor } from 'styles/colors';

const columnLabelWidth = 14;

export const ParaCord = ({ dimensions, data, onSelect, selected = [] }) => {
	const svgRef = useRef(null);
	const linkGroupRef = useRef(null);
	const axisGroupRef = useRef(null);
	const containerRef = useRef(null);
	const selectionRef = useRef({ nodes: [], links: [] });
	const nodesRef = useRef({ nodes: {} });

	const { height, width } = dimensions;
	const [xMargin, yMargin] = [55, 20];

	const {
		nodes,
		links: { links },
		propertyOrder,
	} = data;

	// scale to fill the usable space
	const axisX = useMemo(
		() =>
			d3
				.scaleLinear()
				.domain([0, propertyOrder.length - 1])
				.range([xMargin, width - xMargin]),
		[width, xMargin, propertyOrder]
	);

	// spread the nodes out evenly across the axis working space
	const axisY = useMemo(
		() =>
			d3
				.scaleLinear()
				.domain([0, 1])
				.range([yMargin, height - yMargin]),
		[yMargin, height]
	);

	// scale the opacity of the links based on count
	// const scaleLinkOpacity = useMemo(
	//   () => d3.scaleLinear()
	//     .domain([1, maxLinkCount])
	//     .range([0.15, .6]), [maxLinkCount]);
	//
	// // scale the opacity of the links based on count
	// const scaleSelectedLinkOpacity = useMemo(
	//   () => d3.scaleLinear()
	//     .domain([1, maxLinkCount])
	//     .range([0.45, .8]), [maxLinkCount]);

	// here we set the initial x value
	const procProperties = useMemo(() => {
		return propertyOrder.map((p, i) => {
			p.x = axisX(i);
			return p;
		});
	}, [propertyOrder, axisX]);

	// TODO: Make this a util
	const handleNodeClick = (node) => {
		const n = pick(node, ['propertyKey', 'propertyIndex', 'key']);
		let tempNodes = [];
		const index = selected.findIndex((test) => test.propertyKey === n.propertyKey && test.key === n.key);
		if (index >= 0) {
			selected.splice(index, 1);
			tempNodes = selected;
		} else {
			selected.push(n);
			tempNodes = selected;
		}

		if (onSelect) onSelect([...tempNodes]);
	};

	useEffect(() => {
		// store the current nodes in a ref
		// must store in a ref to ensure that the d3 calls have the correct objects
		// otherwise they get out of sync on data updates (like filtering)
		Object.values(nodes).forEach((column, i) => {
			const count = Object.values(column).length;
			Object.values(column).forEach((node, j) => {
				node.x = axisX(procProperties[i].index);
				node.y = axisY((j + 1) / (count + 1));
			});
		});
		nodesRef.current.nodes = nodes;

		// manage the selection of nodes and links
		// must store in a ref to ensure that the d3 calls have the correct objects
		// otherwise they get out of sync on data updates (like filtering)
		selectionRef.current.nodes = [...selected];
		if (!selected.length) {
			selectionRef.current.links = [];
		} else {
			selectionRef.current.links = links.filter((link) => {
				return link.full.some((s) => selected.map((n) => n.key).includes(s));
			});
		}

		// create a link from the source to the target
		const linkLine = d3
			.linkHorizontal()
			.source((l) => {
				const src = nodesRef.current.nodes[l.source.propertyKey][l.source.value];
				return [src.x, src.y];
			})
			.target((l) => {
				const tgt = nodesRef.current.nodes[l.target.propertyKey][l.target.value];
				return [tgt.x, tgt.y];
			});

		// define the common transition
		const t = d3.select(svgRef.current).transition().duration(750);

		// test if a link points to a given node
		const nodeLinkTest = (link, node) => {
			return link.propertyIndex === node.propertyIndex && link.value === node.key;
		};

		// test if two nodes are equivalent
		const nodeTest = (node1, node2) => {
			return node1.propertyIndex === node2.propertyIndex && node1.key === node2.key;
		};

		// test if the node is in the selection
		const nodeInSelection = (node) => {
			return selectionRef.current.nodes.some((n) => nodeTest(node, n));
		};

		// manage the link stroke opacity's many forms
		const setLinkStrokeOpacity = (link) => {
			if (selectionRef.current.links.length) {
				const filtered = selectionRef.current.links.filter((l) => l.key === link.key);
				return filtered.length > 0 ? 1.0 : 0.2;
			}
			return 0.5;
		};

		// create nodes is needed in two locations, so externalize it
		const createNodes = (d3selection) => {
			// for each node
			const nodeGroup = d3selection
				.append('g')
				.classed('node', true)
				.attr('id', (d) => `node-${d.propertyIndex}-${d.valueIndex}`)
				.attr('transform', (d) => `translate(0 ${d.y})`)
				.attr('cy-test', 'paracord-node')
				.on('click', (_d, evt) => handleNodeClick(evt))
				.on('mouseover', (_d, hoverNode) => {
					// add the hover "border"
					d3.select(this).select('.nodeBorder').attr('stroke', 'black');

					// grey out any non-related links
					d3.select(linkGroupRef.current)
						.selectAll('.link')
						.filter((l) => !l.full.includes(hoverNode.key))
						.transition()
						.duration(250)
						.attr('stroke', 'grey')
						.attr('stroke-opacity', 0.1);

					// grey out any non-related links
					d3.select(linkGroupRef.current)
						.selectAll('.nodeBody')
						.filter((l) => !l.full.includes(hoverNode.key))
						.attr('stroke', 'red')
						.attr('stroke-opacity', 0.1);

					// record all of the unique nodes in the set
					let l = [];

					// highlight any related links
					d3.select(linkGroupRef.current)
						.selectAll('.link')
						.filter((l) => l.full.includes(hoverNode.key))
						.transition()
						.duration(500)
						.attr('stroke', primaryColor)
						.attr('stroke-opacity', 0.9)
						.each((d) => {
							l.push(d.source);
							l.push(d.target);
						});

					// remove duplicates
					l = Array.from(new Set(l));

					// show the node labels
					d3.select(axisGroupRef.current)
						.selectAll('.nodeLabel')
						.filter((d) => l.filter((toTest) => nodeLinkTest(toTest, d)).length)
						.transition()
						.duration(500)
						.style('opacity', 1);
				})
				.on('mouseout', function () {
					// remove the node "border"
					d3.select(this)
						.select('.nodeBorder')
						.attr('stroke', (d) => (nodeInSelection(d) ? 'black' : 'transparent'));

					// return link opacity to normal
					d3.select(linkGroupRef.current)
						.selectAll('.link')
						.transition()
						.duration(250)
						.attr('stroke', primaryColor)
						.attr('stroke-opacity', (d) => setLinkStrokeOpacity(d));

					// hide the node labels
					d3.select(axisGroupRef.current).selectAll('.nodeLabel').transition().duration(250).style('opacity', 0);
				});

			// node "border"
			nodeGroup
				.append('circle')
				.classed('nodeBorder', true)
				.attr('r', 6)
				.attr('fill', 'transparent')
				.attr('stroke', (d) => (nodeInSelection(d) ? 'black' : 'transparent'))
				.attr('stroke-width', 2);

			// node body
			nodeGroup
				.append('circle')
				.classed('nodeBody', true)
				.attr('r', 4)
				.attr('stroke', 'white')
				.attr('fill', primaryColor);

			// create the labels, hidden by default
			const textGroup = nodeGroup
				.append('g')
				.classed('nodeLabel', true)
				.attr('transform', `translate(0 -20)`)
				.attr('cy-test', 'paracord-node-label')
				.style('opacity', 0)
				.style('pointer-events', 'none');

			// fake label so the following path can get a real size
			textGroup
				.append('text')
				.text((d) => truncString(d.label, columnLabelWidth))
				.attr('font-size', 12)
				.attr('text-anchor', 'middle')
				.attr('stroke', 'none')
				.attr('fill', 'none');

			// label background
			textGroup.append('path').attr('d', function (_d) {
				const { width, height } = d3.select(this.parentNode).select('text').node().getBBox();
				const upperLeft = [-width / 2 - 4, -height / 2 - 5];
				return `M ${upperLeft[0]},${upperLeft[1]} 
          l ${width + 8},0 
          l 0,${height + 8} 
          l ${-width / 2},0 
          l -4,4 
          l -4,-4 
          l ${-width / 2},0`;
			});

			// label itself
			textGroup
				.append('text')
				.text((d) => truncString(d.label, columnLabelWidth))
				.attr('z-index', 1)
				.attr('font-size', 12)
				.attr('text-anchor', 'middle')
				.attr('dominant-baseline', 'middle')
				.attr('stroke', 'none')
				.attr('fill', 'white')
				.attr('cy-test', 'paracord-node-label-text');

			// drag support
			nodeGroup.call(
				d3
					.drag()
					.on('start', (event) => {
						event.sourceEvent.stopPropagation();
					})
					.on('drag', function (event, d) {
						const { y } = event;
						d.y = y;
						// move the whole group
						d3.select(this).attr('transform', `translate(0, ${y})`);

						// update the links where this is the source
						d3.select(linkGroupRef.current)
							.selectAll('.link')
							.filter((l) => nodeLinkTest(l.source, d))
							.attr('d', (d) => linkLine(d));

						// update the links where this is the target
						d3.select(linkGroupRef.current)
							.selectAll('.link')
							.filter((l) => nodeLinkTest(l.target, d))
							.attr('d', (d) => linkLine(d));
					})
			);
			return nodeGroup;
		};

		// for each property, in order
		d3.select(axisGroupRef.current)
			.selectAll('.axis')
			.data(procProperties, (d) => d.label)
			.join(
				(enter) => {
					// the axis
					const g = enter
						.append('g')
						.classed('axis', true)
						.attr('transform', (d) => `translate(${d.x} 0)`);

					// axis label, truncated
					g.append('text')
						.attr('text-anchor', 'middle')
						.text((d) => truncString(d.label, 14))
						.attr('transform', `translate(0 14)`)
						.attr('cy-test', 'paracord-axis-label');

					// axis line
					g.append('line')
						.classed('axisLine', true)
						.attr('stroke', 'black')
						.attr('x1', 0)
						.attr('y1', axisY(0))
						.attr('x2', 0)
						.attr('y2', axisY(1));

					// nodes (one per value)
					g.selectAll('.node')
						.data(
							(d) => {
								return Object.values(nodesRef.current.nodes[d.label]);
							},
							(d) => d.key
						)

						.join(
							(enter) => createNodes(enter),
							(update) => update,
							(exit) => {
								exit.remove();
							}
						);

					return g;
				},
				(update) => {
					// axis line
					update
						.call((enter) => enter.transition(t).attr('transform', (d) => `translate(${d.x} 0)`))
						.selectAll('line')
						.attr('y1', axisY(0))
						.attr('y2', axisY(1));

					// update nodes
					update
						.selectAll('.node')
						.data(
							(d) => {
								return Object.values(nodesRef.current.nodes[d.label]);
							},
							(d) => d.key
						)
						.join(
							(enter) => createNodes(enter),
							(update) =>
								update.call((update) => update.transition(t).attr('transform', (d, _i) => `translate(0 ${d.y})`)),
							(exit) => {
								exit.remove();
							}
						);

					// update labels
					update
						.selectAll('.nodeLabel')
						.data(
							(d) => {
								return Object.values(nodesRef.current.nodes[d.label]);
							},
							(d) => d.key
						)
						.join(
							(enter) => createNodes(enter),
							(update) => update.call((update) => update.transition(t)),
							(exit) => {
								exit.remove();
							}
						);
					return update;
				},
				(exit) => {
					exit.remove();
				}
			);

		// links
		d3.select(linkGroupRef.current)
			.selectAll('.link')
			.data(links, (d) => d.key)
			.join(
				(enter) => {
					// link line
					enter
						.append('path')
						.classed('link', true)
						.attr('stroke', primaryColor)
						.attr('fill', 'none')
						.call((enter) =>
							enter
								.transition(t)
								.attr('stroke-opacity', (d) => setLinkStrokeOpacity(d))
								.attr('d', (d) => linkLine(d))
						);
				},
				(update) => {
					// update the links
					update.attr('stroke', primaryColor).call((update) =>
						update
							.transition(t)
							.attr('stroke-opacity', (d) => setLinkStrokeOpacity(d))
							.attr('d', (d) => linkLine(d))
					);
				},
				(exit) => {
					exit.remove();
				}
			);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nodes, links, procProperties, axisY, selected]);

	return (
		<svg cy-test="paracord-data" width={dimensions.width} height={dimensions.height} ref={svgRef}>
			<g ref={containerRef}>
				<g ref={linkGroupRef} />
				<g ref={axisGroupRef} />
			</g>
		</svg>
	);
};

function truncString(str, max, add) {
	add = add || '...';
	return typeof str === 'string' && str.length > max ? str.substring(0, max) + add : str;
}

ParaCord.propTypes = {
	colorScaleType: PropTypes.any,
	data: PropTypes.any,
	columns: PropTypes.any,
	dimensions: PropTypes.object,
};
